/**
 * Luma Widget Plugin
 *
 * TypeScript interface for the native iOS widget plugin.
 * This syncs timer state to the lock screen widget via App Groups.
 */

import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Data structure for widget state
 * Keep in sync with LumaWidgetPlugin.swift
 */
export interface WidgetData {
  taskName: string;
  timerMode: 'maker' | 'manager';
  timerState: 'running' | 'paused' | 'idle' | 'break' | 'complete';
  timeRemaining: number; // seconds (for manager mode countdown)
  elapsedTime: number; // seconds
  sessionDuration: number; // seconds (total goal/duration)
  currentMilestone: number;
  totalMilestones: number;
  timeToNextMilestone: number; // seconds
  lastUpdated: number; // timestamp in milliseconds
}

/**
 * Plugin interface - includes both widget and Live Activity methods.
 * Single registration to avoid "plugin already registered" warning.
 */
interface LumaWidgetPluginInterface {
  // Widget methods
  updateWidgetData(data: WidgetData): Promise<{ success: boolean }>;
  clearWidgetData(): Promise<{ success: boolean }>;
  getWidgetData(): Promise<WidgetData | { hasData: false }>;
  // Live Activity methods
  startLiveActivity?(options: Record<string, unknown>): Promise<{ activityId: string; success: boolean }>;
  updateLiveActivity?(options: Record<string, unknown>): Promise<{ success: boolean }>;
  endLiveActivity?(): Promise<{ success: boolean }>;
  isLiveActivitySupported?(): Promise<{ supported: boolean; enabled: boolean }>;
  addListener?(eventName: string, listenerFunc: () => void): Promise<unknown>;
  removeAllListeners?(): Promise<void>;
}

// Single registration - shared by widgetPlugin and liveActivityService
export const LumaWidgetPlugin = Capacitor.isNativePlatform()
  ? registerPlugin<LumaWidgetPluginInterface>('LumaWidget')
  : null;

/**
 * Check if widget functionality is available
 */
export function isWidgetAvailable(): boolean {
  return Capacitor.getPlatform() === 'ios' && LumaWidgetPlugin !== null;
}

/**
 * Update widget with current timer state
 * Call this whenever timer state changes
 */
export async function updateWidgetData(data: WidgetData): Promise<void> {
  if (!isWidgetAvailable()) {
    console.log('[Widget] Not available on this platform');
    return;
  }

  try {
    await LumaWidgetPlugin!.updateWidgetData(data);
  } catch (error: unknown) {
    const code = (error as { code?: string })?.code;
    if (code !== 'UNIMPLEMENTED') {
      console.error('[Widget] Failed to update data:', error);
    }
  }
}

/**
 * Clear widget data (call when timer stops)
 * Handles UNIMPLEMENTED gracefully - native bridge may not be ready at startup.
 */
export async function clearWidgetData(): Promise<void> {
  if (!isWidgetAvailable() || !LumaWidgetPlugin?.clearWidgetData) {
    return;
  }

  try {
    await LumaWidgetPlugin.clearWidgetData();
  } catch (error: unknown) {
    // UNIMPLEMENTED can occur if native bridge isn't ready; avoid noisy logs
    const code = (error as { code?: string })?.code;
    if (code !== 'UNIMPLEMENTED') {
      console.error('[Widget] Failed to clear data:', error);
    }
  }
}

/**
 * Get current widget data (for debugging)
 */
export async function getWidgetData(): Promise<WidgetData | null> {
  if (!isWidgetAvailable()) {
    return null;
  }

  try {
    const result = await LumaWidgetPlugin!.getWidgetData();
    if ('hasData' in result && !result.hasData) {
      return null;
    }
    return result as WidgetData;
  } catch (error) {
    console.error('[Widget] Failed to get data:', error);
    return null;
  }
}

// MARK: - Milestone Calculation Helper

/**
 * Calculate milestone configuration based on mode and duration
 * Mirrors the logic from MilestoneTracker.tsx
 */
export function getMilestoneConfig(
  mode: 'focus' | 'admin',
  sessionDurationMinutes: number,
  goalMinutes?: number
): { count: number; intervalSeconds: number } {
  if (mode === 'focus') {
    // Maker mode: 6 milestones based on user goal (1–6 hrs)
    const totalSeconds = (goalMinutes || 60) * 60;
    return { count: 6, intervalSeconds: totalSeconds / 6 };
  }

  // Admin/Manager mode: dynamic based on session length
  const durationSeconds = sessionDurationMinutes * 60;

  if (sessionDurationMinutes <= 15) {
    // 15 min or less: 3 milestones
    return { count: 3, intervalSeconds: durationSeconds / 3 };
  } else if (sessionDurationMinutes <= 30) {
    // 16-30 min: 5 milestones
    return { count: 5, intervalSeconds: durationSeconds / 5 };
  } else if (sessionDurationMinutes <= 60) {
    // 31-60 min: 4 milestones (every 15 min)
    return { count: 4, intervalSeconds: durationSeconds / 4 };
  } else {
    // Over 60 min: 5 milestones evenly divided
    return { count: 5, intervalSeconds: durationSeconds / 5 };
  }
}

// MARK: - Hook for Timer Integration

/**
 * Syncs timer state to the widget
 * Add this to your timer effect in usePomodoroTimer.tsx
 *
 * Usage:
 * ```typescript
 * useEffect(() => {
 *   syncWidgetWithTimer({
 *     timerState,
 *     activeTask,
 *     timerMode,
 *     timeRemaining,
 *     elapsedTime,
 *     sessionDuration,
 *   });
 * }, [timerState, activeTask, timerMode, timeRemaining, elapsedTime, sessionDuration]);
 * ```
 */
export function syncWidgetWithTimer(params: {
  timerState: string;
  activeTask: { content: string } | null;
  timerMode: 'maker' | 'manager' | null;
  timeRemaining: number;
  elapsedTime: number;
  sessionDuration: number; // in minutes
}): void {
  const { timerState, activeTask, timerMode, timeRemaining, elapsedTime, sessionDuration } = params;

  // Only sync on iOS
  if (!isWidgetAvailable()) {
    return;
  }

  if (timerState === 'running' && activeTask && timerMode) {
    // Calculate milestone info
    const mode = timerMode === 'maker' ? 'focus' : 'admin';
    const goalMinutes = timerMode === 'maker' ? sessionDuration : undefined;
    const { count, intervalSeconds } = getMilestoneConfig(mode, sessionDuration, goalMinutes);

    const achievedMilestones = Math.min(Math.floor(elapsedTime / intervalSeconds), count);
    const timeToNextMilestone = Math.round(intervalSeconds - (elapsedTime % intervalSeconds));

    updateWidgetData({
      taskName: activeTask.content,
      timerMode: timerMode,
      timerState: timerState as WidgetData['timerState'],
      timeRemaining: timerMode === 'maker' ? 0 : timeRemaining,
      elapsedTime: elapsedTime,
      sessionDuration: sessionDuration * 60,
      currentMilestone: achievedMilestones,
      totalMilestones: count,
      timeToNextMilestone: timeToNextMilestone,
      lastUpdated: Date.now(),
    });
  } else if (timerState === 'paused' && activeTask && timerMode) {
    // Keep showing data but mark as paused
    const mode = timerMode === 'maker' ? 'focus' : 'admin';
    const goalMinutes = timerMode === 'maker' ? sessionDuration : undefined;
    const { count, intervalSeconds } = getMilestoneConfig(mode, sessionDuration, goalMinutes);

    const achievedMilestones = Math.min(Math.floor(elapsedTime / intervalSeconds), count);
    const timeToNextMilestone = Math.round(intervalSeconds - (elapsedTime % intervalSeconds));

    updateWidgetData({
      taskName: activeTask.content,
      timerMode: timerMode,
      timerState: 'paused',
      timeRemaining: timerMode === 'maker' ? 0 : timeRemaining,
      elapsedTime: elapsedTime,
      sessionDuration: sessionDuration * 60,
      currentMilestone: achievedMilestones,
      totalMilestones: count,
      timeToNextMilestone: timeToNextMilestone,
      lastUpdated: Date.now(),
    });
  } else if (timerState === 'idle') {
    // Clear widget when idle
    clearWidgetData();
  }
}
